import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useUser } from "./Context/UserContext";
import "../styles/dashboard.css";
import FormProgress from "./FormProgress";
import Swal from "sweetalert2";
import api from "./axios";

// Total Experience and Previous Experience are hidden from the form for now.
// HIDDEN ONLY - nothing else changed: the values are still loaded, still
// validated against Mindteck Experience, and still saved with the submission.
// Flip this to true to show the two fields again.
const SHOW_TOTAL_AND_PREVIOUS_EXPERIENCE = false;

const isNonTechnicalCategory = (category) =>
    String(category || "").replace(/[^a-z]/gi, "").toLowerCase() === "nontechnical";

export default function Dashboard() {
    const { empId, userInfo } = useUser();
    const [autoSkills, setAutoSkills] = useState([]);
    const [isUpdate, setIsUpdate] = useState(false);
    const [step, setStep] = useState(1);
    const [roles, setRoles] = useState([]);
    const [domains, setDomains] = useState([]);
    const [serviceLines, setServiceLines] = useState([]);
    const [skills, setSkills] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [additionalDetails, setAdditionalDetails] = useState(null);
    const [correctionRequested, setCorrectionRequested] = useState(false);
    const [windowOpen, setWindowOpen] = useState(true);
    const [hasSubmittedThisCycle, setHasSubmittedThisCycle] = useState(false);
    const [softSkills, setSoftSkills] = useState([]);
    // Non-Technical hierarchy: Service Line -> Role -> Skill. These two lists
    // come from the roleskillmapping table (004_RoleSkillMapping.sql) and are
    // narrower than the full `serviceLines` / `roles` lists the Technical
    // hierarchy uses, so a Non-Technical employee can only pick combinations
    // that actually have skills behind them.
    const [nonTechServiceLines, setNonTechServiceLines] = useState([]);
    const [nonTechRoles, setNonTechRoles] = useState([]);
    // The experience figures arrive from two async sources (DOJ on the user and
    // the previous-experience API). validateStep1 compares them, so it must not
    // run until both have landed — otherwise the first "Next" click fails on
    // half-loaded data and the button looks unreliable (SM-EXP-005 / SM-GEN-003).
    const [expLoaded, setExpLoaded] = useState(false);
    // SM-SKILL-MATCH: true while the skill rows hold TMS skills matched before
    // a Domain was chosen, so the Non-Technical effect can clear them (that
    // hierarchy has no Domain). REVERT: delete this ref and its uses.
    const autoMatchedRef = useRef(false);
    const [stepError, setStepError] = useState("");

    const [formData, setFormData] = useState({
        role: "",
        domain: "",
        serviceLine: "",
        location: "",
        softSkill: "",
        trainingRequirement: "",
        trainingRequiredName: "",
    });

    // Non-Technical employees are described by Service Line -> Role -> Skill,
    // with no Domain above it (Enablers / Support has no domain). Step 2 hides
    // Domain for them, drives Role from the chosen Service Line and Skills from
    // the chosen Role, and additionally asks for a Soft Skill.
    const isNonTechnical = isNonTechnicalCategory(additionalDetails?.employeeCategory);
    // SM-SKILL-MATCH: latest value for the async loader below.
    // REVERT: delete these two lines.
    const isNonTechnicalRef = useRef(false);
    isNonTechnicalRef.current = isNonTechnical;

    const [primarySkills, setPrimarySkills] = useState([{ skill: "", level: "", isNameOnly: false }]);
    const [secondarySkills, setSecondarySkills] = useState([{ skill: "", level: "", isNameOnly: false }]);
    const [mindteckExp, setMindteckExp] = useState({ years: "", months: "" });
    const [totalExp, setTotalExp] = useState({ years: "", months: "" });
    const [previousExp, setPreviousExp] = useState({ years: "", months: "" });

    // ─── Fetch dropdowns ───────────────────────────────────────────────────────
    useEffect(() => {
        async function fetchData() {
            const [r, d, s] = await Promise.all([
                axios.get("/api/roles"),
                axios.get("/api/domains"),
                axios.get("/api/serviceLines"),
            ]);
            setRoles(r.data);
            setDomains(d.data);
            setServiceLines(s.data);
        }
        fetchData();
    }, []);

    // ─── Fetch additional employee details ────────────────────────────────────
    useEffect(() => {
        if (!empId) return;

        console.log("Calling additional-details API", empId);

        // axios.get(
        //     `/api/auth/additional-details/${empId}?loggedInUserId=${empId}`
        // )
        api.get(`/api/auth/additional-details/${empId}`
        )
            .then(res => {
                setAdditionalDetails(res.data);
            })
            .catch(err => {
                console.error(err);
            });

    }, [empId]);

    // ─── Fetch previous experience ────────────────────────────────────────────
    useEffect(() => {
        if (!empId) return;

        axios.get(`/api/EmployeeSkill/previous-experience/${empId}`)
            .then(res => {
                setPreviousExp({ years: res.data.years, months: res.data.months });
            })
            .catch(error => {
                if (error.response?.status === 404) {
                    setPreviousExp({ years: 0, months: 0 });
                    return;
                }

                console.error(error);
            })
            .finally(() => {
                setExpLoaded(true);
            });
    }, [empId]);

    // ─── Auto-calculate Mindteck experience from DOJ ──────────────────────────
    useEffect(() => {
        if (userInfo?.doj) {
            const dojDate = new Date(userInfo.doj);
            const today = new Date();
            let years = today.getFullYear() - dojDate.getFullYear();
            let months = today.getMonth() - dojDate.getMonth();
            if (months < 0) { years--; months += 12; }
            setMindteckExp({ years, months });
        }
    }, [userInfo]);

    // ─── Auto-calculate total experience ──────────────────────────────────────
    useEffect(() => {
        const prevYears = parseInt(previousExp.years || 0);
        const prevMonths = parseInt(previousExp.months || 0);
        const mindYears = parseInt(mindteckExp.years || 0);
        const mindMonths = parseInt(mindteckExp.months || 0);

        let totalMonths = prevMonths + mindMonths;
        let totalYears = prevYears + mindYears;

        if (totalMonths >= 12) {
            totalYears += Math.floor(totalMonths / 12);
            totalMonths = totalMonths % 12;
        }

        setTotalExp({ years: totalYears, months: totalMonths });
    }, [previousExp, mindteckExp]);



    // SM-SKILL-MATCH: before a Domain is chosen, the employee's TMS skills are
    // matched by name against the full skill list (GET /api/skills/all) and the
    // matches are shown as real, selected skills with their TMS competency.
    // TMS skills with no match in the skill list are left out. Choosing a
    // Domain afterwards works exactly as before (handleDomainChange).
    //
    // REVERT: restore the previous body, which put every TMS skill name into a
    // read-only row ({ skill: s.name, level: s.competency, isNameOnly: true })
    // and did not call /api/skills/all. Also undo the two other SM-SKILL-MATCH
    // changes below (loadEmployeeSkillData and the hint text).
    const loadAutoSkillsNameOnly = async () => {
        try {
            const [autoRes, allRes] = await Promise.all([
                api.get(`/api/EmployeeSkill/auto/${empId}`),
                axios.get("/api/skills/all")
            ]);

            // Non-Technical employees pick skills through Role, not Domain.
            if (isNonTechnicalRef.current) return;

            const empPrimary = autoRes.data?.primarySkills || [];
            const empSecondary = autoRes.data?.secondarySkills || [];
            const allSkills = allRes.data || [];

            // For matching after domain selection
            setAutoSkills([
                ...empPrimary.map(s => s.name.toLowerCase()),
                ...empSecondary.map(s => s.name.toLowerCase())
            ]);

            const norm = (name) => String(name || "").trim().toLowerCase();
            const matched = [];

            const toRows = (list) =>
                list
                    .map(autoSkill => {
                        const match = allSkills.find(
                            s => norm(s.name) === norm(autoSkill.name)
                        );
                        if (!match) return null;

                        if (!matched.some(m => m.id === match.id)) matched.push(match);

                        return {
                            skill: String(match.id),
                            level: autoSkill.competency || "",
                            isNameOnly: false
                        };
                    })
                    .filter(Boolean);

            const matchedPrimary = toRows(empPrimary);
            const matchedSecondary = toRows(empSecondary);

            // The dropdowns offer only the matched skills until a Domain is
            // chosen; the Domain then supplies its full list as before.
            setSkills(matched);
            autoMatchedRef.current = matched.length > 0;

            setPrimarySkills(
                matchedPrimary.length
                    ? matchedPrimary
                    : [{ skill: "", level: "", isNameOnly: false }]
            );

            setSecondarySkills(
                matchedSecondary.length
                    ? matchedSecondary
                    : [{ skill: "", level: "", isNameOnly: false }]
            );

        } catch (err) {
            /* console.error("Error fetching auto skills:", err);*/
        }
    };

    // ─── Load saved employee skill data (after submit / on mount) ────────────
    // ✅ FIX #1: use lowercase keys (primarySkills/id/competency) to match the
    //    JSON actually returned by the API (System.Text.Json camelCases PascalCase
    //    C# property names). The old PascalCase keys never matched, so this branch
    //    always fell through to the TMS "auto" data instead of the saved record.
    // ✅ FIX #2: removed the unconditional `setIsSubmitted(true)` at the end.
    //    isSubmitted is now owned exclusively by the submission-status effect
    //    below, so a stale/historical record no longer locks a newly-opened
    //    review cycle.
    const loadEmployeeSkillData = async () => {
        try {
            const res = await api.get(`/api/EmployeeSkill/${empId}`);
            const data = res.data;

            const autoRes = await api.get(`/api/EmployeeSkill/auto/${empId}`);
            const autoData = autoRes.data;

            // SM-SKILL-MATCH: no saved submission yet, so show the employee's
            // TMS skills that exist in the skill list. Previously nothing was
            // shown here until a Domain was chosen.
            // REVERT: change back to  if (!data) return;
            if (!data) {
                await loadAutoSkillsNameOnly();
                return;
            }

            setFormData({
                role: data.roleId || "",
                domain: data.domainId || "",
                serviceLine: data.serviceLineId || "",
                certification: data.certification || "",
                certificationName: data.certificationName || "",
                trainingReceived: data.trainingReceived || "",
                trainingName: data.trainingName || "",
                additionalSkill: data.additionalSkill || "",
                softSkill: data.softSkill || ""
            });

            // Saved skills from EmployeeSkill record
            const savedPrimary = data.primarySkills || [];
            const savedSecondary = data.secondarySkills || [];

            let allSkills = [];

            // ============================================================
            // 1. TECHNICAL EMPLOYEE
            // ============================================================
            if (data.domainId) {

                const skillRes = await axios.get(
                    `/api/skills/by-domain/${data.domainId}`
                );

                allSkills = skillRes.data || [];
                setSkills(allSkills);

            }
            // ============================================================
            // 2. NON-TECHNICAL EMPLOYEE
            // ============================================================
            else if (data.roleId) {

                // Non-Technical employees have no DomainId: their skill list
                // hangs off Role (Service Line → Role → Skill).
                const query = data.serviceLineId
                    ? `?serviceLineId=${data.serviceLineId}`
                    : "";

                const skillRes = await axios.get(
                    `/api/skills/by-role/${data.roleId}${query}`
                );

                allSkills = skillRes.data || [];
                setSkills(allSkills);
            }

            // ============================================================
            // PRIMARY SKILLS
            // ============================================================

            let mappedPrimary = [];

            if (savedPrimary.length > 0) {

                // First priority:
                // Use skills saved in EmployeeSkill table
                mappedPrimary = savedPrimary.map(s => ({
                    skill: String(s.id),
                    level: s.competency || "",
                    isNameOnly: false
                }));

            } else if (data.domainId && autoData?.primarySkills?.length > 0) {

                // If there are no saved skills, use auto skills
                mappedPrimary = autoData.primarySkills
                    .map(s => {

                        const match = allSkills.find(
                            a =>
                                a.name?.trim().toLowerCase() ===
                                s.name?.trim().toLowerCase()
                        );

                        if (!match) return null;

                        return {
                            skill: String(match.id),
                            level: s.competency || "",
                            isNameOnly: false
                        };
                    })
                    .filter(Boolean);
            }

            // ============================================================
            // SECONDARY SKILLS
            // ============================================================

            let mappedSecondary = [];

            if (savedSecondary.length > 0) {

                // First priority:
                // Use skills saved in EmployeeSkill table
                mappedSecondary = savedSecondary.map(s => ({
                    skill: String(s.id),
                    level: s.competency || "",
                    isNameOnly: false
                }));

            } else if (data.domainId && autoData?.secondarySkills?.length > 0) {

                // If there are no saved skills, use auto skills
                mappedSecondary = autoData.secondarySkills
                    .map(s => {

                        const match = allSkills.find(
                            a =>
                                a.name?.trim().toLowerCase() ===
                                s.name?.trim().toLowerCase()
                        );

                        if (!match) return null;

                        return {
                            skill: String(match.id),
                            level: s.competency || "",
                            isNameOnly: false
                        };
                    })
                    .filter(Boolean);
            }

            // ============================================================
            // SET PRIMARY SKILLS
            // ============================================================

            setPrimarySkills(
                mappedPrimary.length > 0
                    ? mappedPrimary
                    : [
                        {
                            skill: "",
                            level: "",
                            isNameOnly: false
                        }
                    ]
            );

            // ============================================================
            // SET SECONDARY SKILLS
            // ============================================================

            setSecondarySkills(
                mappedSecondary.length > 0
                    ? mappedSecondary
                    : [
                        {
                            skill: "",
                            level: "",
                            isNameOnly: false
                        }
                    ]
            );

            setIsUpdate(true);

        } catch (error) {

            if (error.response?.status === 404) {

                setIsUpdate(false);

                // For a new employee, load auto skills
                await loadAutoSkillsNameOnly();

                return;
            }

            console.error("Error loading employee skill data:", error);
        }
    };

    useEffect(() => {
        if (empId) loadEmployeeSkillData();
    }, [empId]);

    // ✅ Single source of truth for the employee's submit-button state.
    // The backend (/api/Unfreeze/submission-status/{empId}) already knows,
    // for the CURRENT cycle: whether the window is open, and whether this
    // employee has already submitted in it — so we no longer need to fetch
    // the skill record separately and diff timestamps on the client.
    const fetchSubmissionStatus = async () => {
        try {
            const res = await api.get(`/api/Unfreeze/submission-status/${empId}`);
            const {
                isWindowOpen,
                employeeSubmitted,
                correctionRequested: correction,
            } = res.data;

            // Locked if the window is closed OR the employee already
            // submitted their own skills in the current cycle. A manager's
            // correction request reopens the form (SM-MGR-010) — the backend
            // already reflects that in employeeSubmitted, this flag only
            // drives the banner.
            //
            // The two reasons are tracked separately: a closed window is not
            // the same as having submitted, and telling someone their skills
            // "have been submitted" when they never were is misleading.
            setWindowOpen(Boolean(isWindowOpen));
            setHasSubmittedThisCycle(Boolean(employeeSubmitted));
            setIsSubmitted(!isWindowOpen || employeeSubmitted);
            setCorrectionRequested(Boolean(correction));
        } catch (error) {
            // if (process.env.NODE_ENV !== "production") console.error("Submission status check failed:", error);
        }
    };

    useEffect(() => {
        if (empId) fetchSubmissionStatus();
    }, [empId]);

    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: typeof value === "string" ? value.replace(/^\s+/, "") : value
        });
    };

    // Non-Technical: changing a level of the hierarchy invalidates everything
    // below it, so the stale Role / Skill selections are cleared rather than
    // left pointing at a combination that is no longer mapped.
    const handleNonTechServiceLineChange = (e) => {
        const serviceLineId = e.target.value;

        setFormData(prev => ({ ...prev, serviceLine: serviceLineId, role: "" }));
        setSkills([]);
        setPrimarySkills([{ skill: "", level: "", isNameOnly: false }]);
        setSecondarySkills([{ skill: "", level: "", isNameOnly: false }]);
    };

    const handleNonTechRoleChange = (e) => {
        const roleId = e.target.value;

        setFormData(prev => ({ ...prev, role: roleId }));
        setPrimarySkills([{ skill: "", level: "", isNameOnly: false }]);
        setSecondarySkills([{ skill: "", level: "", isNameOnly: false }]);
    };

    const handleDomainChange = async (e) => {
        const domainId = parseInt(e.target.value, 10);

        setFormData(prev => ({
            ...prev,
            domain: domainId || ""
        }));

        if (!domainId) {
            setSkills([]);
            await loadAutoSkillsNameOnly();
            return;
        }

        try {
            const skillRes = await axios.get(
                `/api/skills/by-domain/${domainId}`
            );

            const domainSkills = skillRes.data;
            setSkills(domainSkills);

            const autoRes = await api.get(`/api/EmployeeSkill/auto/${empId}`);
            const autoPrimary = autoRes.data.primarySkills || [];
            const autoSecondary = autoRes.data.secondarySkills || [];

            // Match Primary Skills
            const matchedPrimary = autoPrimary
                .map(autoSkill => {
                    const match = domainSkills.find(
                        ds =>
                            ds.name.trim().toLowerCase() ===
                            autoSkill.name.trim().toLowerCase()
                    );

                    if (!match) return null;

                    return {
                        skill: String(match.id),
                        level: autoSkill.competency || "",
                        isNameOnly: false
                    };
                })
                .filter(Boolean);

            // Match Secondary Skills
            const matchedSecondary = autoSecondary
                .map(autoSkill => {
                    const match = domainSkills.find(
                        ds =>
                            ds.name.trim().toLowerCase() ===
                            autoSkill.name.trim().toLowerCase()
                    );

                    if (!match) return null;

                    return {
                        skill: String(match.id),
                        level: autoSkill.competency || "",
                        isNameOnly: false
                    };
                })
                .filter(Boolean);

            setPrimarySkills(
                matchedPrimary.length
                    ? matchedPrimary
                    : [{ skill: "", level: "", isNameOnly: false }]
            );

            setSecondarySkills(
                matchedSecondary.length
                    ? matchedSecondary
                    : [{ skill: "", level: "", isNameOnly: false }]
            );

        } catch (err) {
            /* console.error(err);*/
        }
    };

    const handleSkillChange = (index, type, field, value) => {
        // A competency level belongs to a skill. Clearing the skill back to
        // "Select Skill" must therefore clear the level with it - otherwise the
        // row keeps a level that is about to be discarded on submit (the
        // payload filters rows with no skill), and the form shows a competency
        // the employee never actually recorded against anything.
        const apply = (item) => {
            const next = { ...item, [field]: value };

            if (field === "skill" && !value) next.level = "";

            return next;
        };

        if (type === "primary") {
            setPrimarySkills(primarySkills.map((item, i) =>
                i === index ? apply(item) : item
            ));
        } else {
            setSecondarySkills(secondarySkills.map((item, i) =>
                i === index ? apply(item) : item
            ));
        }
    };

    const addSkill = (type) => {
        if (type === "primary" && primarySkills.length < 3) {
            setPrimarySkills([...primarySkills, { skill: "", level: "", isNameOnly: false }]);
        }
        if (type === "secondary" && secondarySkills.length < 3) {
            setSecondarySkills([...secondarySkills, { skill: "", level: "", isNameOnly: false }]);
        }
    };

    const removeSkill = (index, type) => {
        if (type === "primary") {
            setPrimarySkills(primarySkills.filter((_, i) => i !== index));
        } else {
            setSecondarySkills(secondarySkills.filter((_, i) => i !== index));
        }
    };

    const getSelectedSkillIds = () => {
        const primaryIds = primarySkills.map(s => s.skill).filter(Boolean);
        const secondaryIds = secondarySkills.map(s => s.skill).filter(Boolean);
        return [...primaryIds, ...secondaryIds];
    };

    // ─── Validation ───────────────────────────────────────────────────────────
    const validateStep1 = () => {
        // Both experience figures are read-only and derived server-side, so
        // until they have both loaded there is nothing meaningful to compare.
        // Running the check early is what made the first "Next" click fail.
        // handleSubmit re-runs the same check before saving, so skipping it
        // here never lets bad data through.
        if (!expLoaded) return true;

        const totalYears = parseInt(totalExp.years || 0);
        const totalMonths = parseInt(totalExp.months || 0);
        const mindYears = parseInt(mindteckExp.years || 0);
        const mindMonths = parseInt(mindteckExp.months || 0);

        if ((totalYears * 12 + totalMonths) < (mindYears * 12 + mindMonths)) {
            const message = "Total Experience cannot be less than Mindteck Experience.";
            setStepError(message);
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: message,
                confirmButtonColor: "#d33"
            });
            return false;
        }
        return true;
    };

    // One handler for the Next button so a click always has a visible result:
    // it either advances (and scrolls the new step into view) or says why not.
    const goToStep = (nextStep) => {
        setStepError("");

        if (nextStep === 2 && !validateStep1()) return;

        setStep(nextStep);
        window.scrollTo({ top: 0, behavior: "auto" });
    };

    const validateStep2 = () => {
        // ✅ FIX (SM-GEN-004 / SM-EXP-007): build the missing-field list dynamically
        // so the message always names exactly what's missing, whether it's 1 field or 3.
        const fieldLabels = {
            domain: "Domain",
            serviceLine: "Service Line",
            role: "Role",
            softSkill: "Soft Skill"
        };
        // Non-Technical employees have no Domain (Enablers / Support sits at the
        // top of their hierarchy), so requiring one would make the form
        // unsubmittable. They still pick Service Line and Role - those drive the
        // skill list - plus a Soft Skill.
        const requiredFields = isNonTechnical
            ? ["serviceLine", "role", "softSkill"]
            : ["domain", "serviceLine", "role"];
        const missingFields = requiredFields.filter(field => !formData[field]);

        if (missingFields.length > 0) {
            const missingLabels = missingFields.map(f => fieldLabels[f]).join(", ");
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: missingFields.length === 1
                    ? `${missingLabels} is required.`
                    : `The following fields are required: ${missingLabels}.`,
                confirmButtonColor: "#d33"
            });
            return false;
        }

        const allSkills = [...primarySkills, ...secondarySkills];
        const selectedSkills = allSkills.filter(s => s.skill && !s.isNameOnly);

        // Both categories now have a skill list behind them - Domain for
        // Technical, Role for Non-Technical - so at least one skill is required
        // either way. (handleSubmit enforces the same rule.)
        if (selectedSkills.length === 0) {
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: "Please select at least one Primary or Secondary skill.",
                confirmButtonColor: "#d33"
            });
            return false;
        }

        // ✅ FIX: name the specific skill missing a competency level, not "all selected skills"
        for (const s of allSkills) {
            if (s.skill && !s.isNameOnly && !s.level) {
                const skillName = skills.find(sk => String(sk.id) === String(s.skill))?.name || "the selected skill";
                Swal.fire({
                    icon: "error",
                    title: "Validation Error",
                    text: `Please select a competency level for "${skillName}".`,
                    confirmButtonColor: "#d33"
                });
                return false;
            }
        }

        // ✅ FIX: name exactly which of Certification / Training is unanswered
        const missingYesNo = [];
        if (!formData.certification) missingYesNo.push("Last Completed Certification");
        if (!formData.trainingReceived) missingYesNo.push("Most Recent Training");

        if (missingYesNo.length > 0) {
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: missingYesNo.length === 1
                    ? `Please answer: ${missingYesNo[0]}.`
                    : `Please answer the following: ${missingYesNo.join(", ")}.`,
                confirmButtonColor: "#d33"
            });
            return false;
        }

        if (formData.certification === "Yes" && !formData.certificationName?.trim()) {
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: "Please enter Certification Name.",
                confirmButtonColor: "#d33"
            });
            return false;
        }

        if (formData.trainingReceived === "Yes" && !formData.trainingName?.trim()) {
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: "Please enter Training Name.",
                confirmButtonColor: "#d33"
            });
            return false;
        }

        return true;
    };

    // ─── Submit ───────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();

        const hasPrimarySkill = primarySkills.some(
            s => s.skill && !s.isNameOnly
        );

        const hasSecondarySkill = secondarySkills.some(
            s => s.skill && !s.isNameOnly
        );

        // Both Technical and Non-Technical employees must select
        // at least one Primary or Secondary skill.
        if (!hasPrimarySkill && !hasSecondarySkill) {
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: "Please select at least one Primary or Secondary skill.",
                confirmButtonColor: "#d33"
            });
            return;
        }

        if (!validateStep2()) return;

        // Validate experience
        const totalYears = parseInt(totalExp.years || 0);
        const totalMonths = parseInt(totalExp.months || 0);
        const mindYears = parseInt(mindteckExp.years || 0);
        const mindMonths = parseInt(mindteckExp.months || 0);

        if (
            (totalYears * 12 + totalMonths) <
            (mindYears * 12 + mindMonths)
        ) {
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: "Total Experience cannot be less than Mindteck Experience.",
                confirmButtonColor: "#d33"
            });
            return;
        }

        setLoading(true);

        const payload = {
            employeeId: empId,
            employeeName: userInfo?.name,
            reportingManagerId: userInfo?.reportingMgrId,
            reportingManagerName: userInfo?.reportingMgrName,

            roleId: Number(formData.role) || null,
            domainId: Number(formData.domain) || null,
            serviceLineId: Number(formData.serviceLine) || null,

            designation: additionalDetails?.jobTitle_Desc || null,

            additionalSkill: formData.additionalSkill || null,

            // Separate Soft Skill
            softSkill: formData.softSkill || null,

            isSubmitted: 1,

            totalExperience: {
                years: totalYears,
                months: totalMonths
            },

            mindteckExperience: {
                years: mindYears,
                months: mindMonths
            },

            previousExperience: {
                years: parseInt(previousExp.years || 0),
                months: parseInt(previousExp.months || 0)
            },

            // Primary Skills
            primarySkills: primarySkills
                .filter(s => s.skill && !s.isNameOnly)
                .map(s => ({
                    skill: Number(s.skill),
                    level: s.level || null
                })),

            // Secondary Skills
            secondarySkills: secondarySkills
                .filter(s => s.skill && !s.isNameOnly)
                .map(s => ({
                    skill: Number(s.skill),
                    level: s.level || null
                })),

            certification: formData.certification || null,
            certificationName: formData.certificationName || null,

            trainingReceived: formData.trainingReceived || null,
            trainingName: formData.trainingName || null
        };

        if (process.env.NODE_ENV !== "production") {
            console.log("Saved Payload:", payload);
        }

        try {
            await api.post("/api/EmployeeSkill/save", payload);

            Swal.fire({
                icon: "success",
                title: "Success",
                text: "Details saved successfully!",
                confirmButtonColor: "#3085d6"
            });

            setIsSubmitted(true);
            setCorrectionRequested(false);

            await loadEmployeeSkillData();
            await fetchSubmissionStatus();

        } catch (error) {

            if (error.response?.status === 409) {

                Swal.fire({
                    icon: "warning",
                    title: "Already Submitted",
                    text:
                        error.response?.data?.message ||
                        "You have already submitted for the current cycle.",
                    confirmButtonColor: "#3085d6"
                });

                setIsSubmitted(true);

            } else {

                console.error("Save Employee Skill Error:", error);

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Something went wrong while saving.",
                });
            }

        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const fetchSoftSkills = async () => {
            try {
                const response = await axios.get("/api/SoftSkills");

                setSoftSkills(response.data);
            } catch (error) {
                console.error("Error fetching soft skills:", error);
                setSoftSkills([]);
            }
        };

        fetchSoftSkills();
    }, []);
    // ─── Skill row renderer (handles both name-only and dropdown modes) ───────
    const renderSkillRow = (item, index, type) => (
        <div key={index} className="skill-row">
            {item.isNameOnly ? (
                <div className="skill-nameonly-wrap">
                    <label
                        htmlFor={`${type}-readonly-skill-${index}`}
                        className="sr-only"
                    >
                        {type === "primary"
                            ? `Primary Skill ${index + 1}`
                            : `Secondary Skill ${index + 1}`}
                    </label>

                    <input
                        id={`${type}-readonly-skill-${index}`}
                        className="skill-select readonly-field"
                        value={item.skill}
                        disabled
                        title="Select a domain to enable skill selection"
                    />

                    {item.level && (
                        <span className={`badge-mt badge-${item.level.toLowerCase()}`}>
                            {item.level}
                        </span>
                    )}
                </div>
            ) : (
                <>
                    <label
                        htmlFor={`${type}-skill-${index}`}
                        className="sr-only"
                    >
                        {type === "primary"
                            ? `Primary Skill ${index + 1}`
                            : `Secondary Skill ${index + 1}`}
                    </label>

                    <select
                        id={`${type}-skill-${index}`}
                        value={item.skill || ""}
                        onChange={(e) =>
                            handleSkillChange(index, type, "skill", e.target.value)
                        }
                        disabled={isSubmitted}
                        className="skill-select"
                    >
                        <option value="">Select Skill</option>
                        {skills.map((s) => {
                            const selectedSkills = getSelectedSkillIds();
                            const isSelectedElsewhere =
                                selectedSkills.includes(String(s.id)) &&
                                s.id.toString() !== item.skill;

                            return (
                                <option
                                    key={s.id}
                                    value={String(s.id)}
                                    disabled={isSelectedElsewhere}
                                >
                                    {s.name}
                                </option>
                            );
                        })}
                    </select>
                </>
            )}

            <label
                htmlFor={`${type}-competency-${index}`}
                className="sr-only"
            >
                {type === "primary"
                    ? `Primary Skill ${index + 1} Competency`
                    : `Secondary Skill ${index + 1} Competency`}
            </label>

            {/* A level with no skill attached is meaningless - the submit
                payload drops those rows - so this stays locked until a skill
                is picked, rather than accepting input it will throw away
                without telling the employee. */}
            <select
                id={`${type}-competency-${index}`}
                value={item.level || ""}
                onChange={(e) =>
                    handleSkillChange(index, type, "level", e.target.value)
                }
                disabled={isSubmitted || item.isNameOnly || !item.skill}
                title={
                    !item.skill && !item.isNameOnly
                        ? "Select a skill first"
                        : undefined
                }
                className={`competency-dropdown ${item.level?.toLowerCase() || ""}`}
            >
                <option value="">-Competency-</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Expert">Expert</option>
            </select>

            {/* SM-EMP-006 / SM-EMP-016: once the cycle is submitted the row is
                read-only, so removing a skill must be off the table too. */}
            {!isSubmitted &&
                (type === "primary" ? primarySkills : secondarySkills).length > 1 && (
                    <button
                        type="button"
                        className="remove-btn"
                        aria-label={`Remove ${type === "primary" ? "Primary" : "Secondary"
                            } Skill ${index + 1}`}
                        onClick={() => removeSkill(index, type)}
                    >
                        ✕
                    </button>
                )}
        </div>
    );
    // ─── Non-Technical hierarchy: Service Line → Role → Skill ─────────────────
    // Three cascading loads. Each falls back to the unfiltered master list if
    // the mapping table returns nothing, so an un-migrated database degrades to
    // the old behaviour instead of an unusable form.

    // 1. Service Lines that have Role → Skill mappings under them.
    useEffect(() => {
        if (!isNonTechnical) return;

        axios.get("/api/servicelines/non-technical")
            .then(res => {
                setNonTechServiceLines(res.data?.length ? res.data : serviceLines);
            })
            .catch(error => {
                console.error("Error loading Non-Technical service lines:", error);
                setNonTechServiceLines(serviceLines);
            });
    }, [isNonTechnical, serviceLines]);

    // 2. Roles mapped under the chosen Service Line.
    useEffect(() => {
        if (!isNonTechnical) return;

        if (!formData.serviceLine) {
            setNonTechRoles([]);
            return;
        }

        axios.get(`/api/roles/by-serviceline/${formData.serviceLine}`)
            .then(res => {
                setNonTechRoles(res.data?.length ? res.data : roles);
            })
            .catch(error => {
                console.error("Error loading Non-Technical roles:", error);
                setNonTechRoles(roles);
            });
    }, [isNonTechnical, formData.serviceLine, roles]);

    // 3. Skills offered by the chosen Role.
    useEffect(() => {
        if (!isNonTechnical) return;

        if (!formData.role) {
            setSkills([]);

            // SM-SKILL-MATCH: the TMS match is for Technical employees only;
            // Non-Technical rows stay empty until a Role is chosen, as before.
            // REVERT: delete this block.
            if (autoMatchedRef.current) {
                autoMatchedRef.current = false;
                setPrimarySkills([{ skill: "", level: "", isNameOnly: false }]);
                setSecondarySkills([{ skill: "", level: "", isNameOnly: false }]);
            }
            return;
        }

        const query = formData.serviceLine
            ? `?serviceLineId=${formData.serviceLine}`
            : "";

        axios.get(`/api/skills/by-role/${formData.role}${query}`)
            .then(res => {
                setSkills(res.data || []);
            })
            .catch(error => {
                console.error("Error loading Non-Technical skills:", error);
                setSkills([]);
            });
    }, [isNonTechnical, formData.role, formData.serviceLine]);
    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="dashboard-container">
            <div className="form-card card-mt reveal">
                <FormProgress
                    formData={formData}
                    primarySkills={primarySkills}
                    secondarySkills={secondarySkills}
                    mindteckExp={mindteckExp}
                    totalExp={totalExp}
                />

                <div className="form-stepper" aria-hidden="true">
                    <span className={`stepper-dot ${step >= 1 ? "active" : ""}`}>1</span>
                    <span className={`stepper-line ${step >= 2 ? "active" : ""}`} />
                    <span className={`stepper-dot ${step >= 2 ? "active" : ""}`}>2</span>
                </div>

                <h2 className="form-title">
                    {step === 1
                        ? "Employee Details & Experience"
                        : "Skills, Certifications, Upskilling & Reskilling"}
                </h2>
                <p className="form-subtitle">
                    {step === 1
                        ? "Step 1 of 2 — Review your profile and experience details."
                        : "Step 2 of 2 — Select your skills, competency levels and training."}
                </p>

                {/* SM-MGR-010: the manager routed this submission back. */}
                {correctionRequested && !isSubmitted && (
                    <div className="notice notice-warning" role="status">
                        <span>
                            Your reporting manager has requested corrections. Update your
                            skills and competency levels below, then submit again.
                        </span>
                    </div>
                )}

                {/* SM-EMP-006 / SM-EMP-016: the record is read-only once submitted,
                    and the form says why rather than just greying fields out.
                    A closed window and an existing submission are different
                    reasons and must not share a message. */}
                {isSubmitted && (
                    <div className="notice notice-info" role="status">
                        {hasSubmittedThisCycle ? (
                            <span>
                                Your skills for this cycle have been submitted and are now
                                read-only. They can be changed again in the next unfreeze
                                cycle.
                            </span>
                        ) : (
                            <span>
                                The skill submission window is currently closed, so this form
                                is read-only. Your details will become editable when HR opens
                                the next unfreeze cycle.
                            </span>
                        )}
                    </div>
                )}

                {stepError && (
                    <div className="notice notice-error" role="alert">
                        <span>{stepError}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="form-grid">

                    {/* ── STEP 1 ── */}
                    {step === 1 && (
                        <>
                            <div className="form-group">
                                <label htmlFor="employeeId">Employee ID</label>
                                <input
                                    id="employeeId"
                                    value={empId || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="employeeName">Employee Name</label>
                                <input
                                    id="employeeName"
                                    value={userInfo?.name || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            {additionalDetails && (
                                <>
                                    <div className="form-group">
                                        <label htmlFor="businessType">Business Type</label>

                                        <input
                                            id="businessType"
                                            value={additionalDetails.businessType || ""}
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="region">Region</label>

                                        <input
                                            id="region"
                                            value={additionalDetails.region || ""}
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="subRegion">Sub Region</label>

                                        <input
                                            id="subRegion"
                                            value={additionalDetails.subRegion || ""}
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="workLocation">Work Location</label>

                                        <input
                                            id="workLocation"
                                            value={additionalDetails.workLocation || ""}
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="department">Department</label>

                                        <input
                                            id="department"
                                            value={additionalDetails.department || ""}
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="employeeCategory">Employee Category</label>

                                        <input
                                            id="employeeCategory"
                                            value={additionalDetails.employeeCategory || ""}
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="designation">Employee Designation</label>

                                        <input
                                            id="designation"
                                            value={additionalDetails.jobTitle_Desc || ""}
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                                </>
                            )}

                            <div className="form-group">
                                <label htmlFor="reportingManagerId">Reporting Manager ID</label>

                                <input
                                    id="reportingManagerId"
                                    value={userInfo?.reportingMgrId || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="reportingManagerName">Reporting Manager Name</label>

                                <input
                                    id="reportingManagerName"
                                    value={userInfo?.reportingMgrName || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            {/* Total Experience — hidden, see
                                SHOW_TOTAL_AND_PREVIOUS_EXPERIENCE */}
                            {SHOW_TOTAL_AND_PREVIOUS_EXPERIENCE && (
                            <div className="form-group">
                                <label htmlFor="totalExpYears">
                                    Total Experience <span className="required-star">*</span>
                                </label>
                                <div className="experience-dropdowns">
                                    <div>
                                        <label htmlFor="totalExpYears" className="sr-only">
                                            Total Experience Years
                                        </label>
                                        <select
                                            id="totalExpYears"
                                            value={totalExp.years || ""}
                                            disabled
                                            className="readonly-field"
                                        >
                                            <option value="">Years</option>
                                            {Array.from({ length: 40 }, (_, i) => (
                                                <option key={i} value={i}>{i} {i === 1 ? "Year" : "Years"}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label htmlFor="totalExpMonths" className="sr-only">
                                            Total Experience Months
                                        </label>
                                        <select
                                            id="totalExpMonths"
                                            value={totalExp.months || ""}
                                            disabled
                                            className="readonly-field"
                                        >
                                            <option value="">Months</option>
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <option key={i} value={i}>{i} {i === 1 ? "Month" : "Months"}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Previous Experience — hidden, see
                                SHOW_TOTAL_AND_PREVIOUS_EXPERIENCE */}
                            {SHOW_TOTAL_AND_PREVIOUS_EXPERIENCE && (
                            <div className="form-group">
                                <label>Previous Experience</label>
                                <div className="experience-dropdowns">
                                    <div>
                                        <label htmlFor="previousExpYears" className="sr-only">
                                            Previous Experience Years
                                        </label>
                                        <select
                                            id="previousExpYears"
                                            value={previousExp.years ?? ""}
                                            disabled
                                            className="readonly-field"
                                        >
                                            <option value="">Years</option>
                                            {Array.from({ length: 40 }, (_, i) => (
                                                <option key={i} value={i}>{i} {i === 1 ? "Year" : "Years"}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label htmlFor="previousExpMonths" className="sr-only">
                                            Previous Experience Months
                                        </label>
                                        <select
                                            id="previousExpMonths"
                                            value={previousExp.months ?? ""}
                                            disabled
                                            className="readonly-field"
                                        >
                                            <option value="">Months</option>
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <option key={i} value={i}>{i} {i === 1 ? "Month" : "Months"}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Mindteck Experience */}
                            <div className="form-group">
                                <label>Mindteck Experience</label>
                                <div className="experience-dropdowns">
                                    <div>
                                        <label htmlFor="mindteckExpYears" className="sr-only">
                                            Mindteck Experience Years
                                        </label>
                                        <select
                                            id="mindteckExpYears"
                                            value={mindteckExp.years || ""}
                                            disabled
                                            className="readonly-field"
                                        >
                                            <option value="">Years</option>
                                            {Array.from({ length: 40 }, (_, i) => (
                                                <option key={i} value={i}>{i} {i === 1 ? "Year" : "Years"}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label htmlFor="mindteckExpMonths" className="sr-only">
                                            Mindteck Experience Months
                                        </label>
                                        <select
                                            id="mindteckExpMonths"
                                            value={mindteckExp.months || ""}
                                            disabled
                                            className="readonly-field"
                                        >
                                            <option value="">Months</option>
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <option key={i} value={i}>{i} {i === 1 ? "Month" : "Months"}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="form-group full-width button-row">
                                <button
                                    type="button"
                                    className="submit-btn small-btn next-btn"
                                    onClick={() => goToStep(2)}
                                >
                                    Next →
                                </button>
                            </div>
                        </>
                    )}

                    {/* ── STEP 2 ── */}
                    {step === 2 && (
                        <>
                            {/* Technical: Domain → Service Line → Role.
                                Non-Technical: Service Line → Role → Skill, with
                                no Domain above it (rendered further below). */}
                            {!isNonTechnical && (
                            <>
                            <div className="form-group">
                                <label htmlFor="domain">
                                    Domain<span className="required-star">*</span>
                                </label>

                                <select
                                    id="domain"
                                    name="domain"
                                    value={formData.domain}
                                    onChange={handleDomainChange}
                                    disabled={isSubmitted}
                                    required
                                >
                                    <option value="">Select Domain</option>
                                            {domains
                                                .filter((d) => Number(d.id) !== 7)
                                                .map((d) => (
                                                    <option key={d.id} value={d.id}>
                                                        {d.name}
                                                    </option>
                                                ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="serviceLine">
                                    Service Line<span className="required-star">*</span>
                                </label>

                                <select
                                    id="serviceLine"
                                    name="serviceLine"
                                    value={formData.serviceLine}
                                    onChange={handleChange}
                                    disabled={isSubmitted}
                                    required
                                >
                                    <option value="">Select</option>
                                    {serviceLines.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="role">
                                    Role<span className="required-star">*</span>
                                </label>

                                <select
                                    id="role"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    disabled={isSubmitted}
                                >
                                    <option value="">Select Role</option>
                                    {roles
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map((r) => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                </select>
                            </div>
                            </>
                            )}

                            {/* Non-Technical: Service Line → Role. No Domain —
                                Enablers / Support is the top of this hierarchy.
                                Role drives the skill dropdowns below. */}
                            {isNonTechnical && (
                            <>
                            <div className="form-group">
                                <label htmlFor="nt-serviceLine">
                                    Service Line<span className="required-star">*</span>
                                </label>

                                <select
                                    id="nt-serviceLine"
                                    name="serviceLine"
                                    value={formData.serviceLine}
                                    onChange={handleNonTechServiceLineChange}
                                    disabled={isSubmitted}
                                    required
                                >
                                    <option value="">Select Service Line</option>
                                    {nonTechServiceLines.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="nt-role">
                                    Role<span className="required-star">*</span>
                                </label>

                                <select
                                    id="nt-role"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleNonTechRoleChange}
                                    disabled={isSubmitted || !formData.serviceLine}
                                    required
                                >
                                    <option value="">Select Role</option>
                                    {[...nonTechRoles]
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map((r) => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                </select>

                                {!formData.serviceLine && (
                                    <p className="info-hint">
                                        Select a service line above to enable role selection
                                    </p>
                                )}
                            </div>
                            </>
                            )}

                            {/* Primary Skills */}
                            <div className="full-width">
                                <h3 className="form-section-title">
                                    Primary Skills (Max 3)
                                    <span className="required-star" aria-label="at least one skill required across primary or secondary">*</span>
                                </h3>
                                <p className="info-hint">
                                    At least one Primary or Secondary skill with a competency level is required.
                                </p>
                                {isNonTechnical
                                    ? !formData.role && (
                                        <p className="info-hint">
                                            Select a role above to enable skill selection
                                        </p>
                                    )
                                    : !formData.domain && (
                                        /* SM-SKILL-MATCH: explain the pre-filled skills.
                                           REVERT: keep only the second message. */
                                        <p className="info-hint">
                                            {skills.length > 0
                                                ? "These are your TMS skills that match the skill list. Select a domain above to choose from all skills in that domain."
                                                : "Select a domain above to enable skill selection"}
                                        </p>
                                    )}
                                <div className="skill-block">
                                    {primarySkills.map((item, index) =>
                                        renderSkillRow(item, index, "primary")
                                    )}
                                    {primarySkills.length < 3 && !isSubmitted && (
                                        <button
                                            type="button"
                                            className="add-skill-btn"
                                            onClick={() => addSkill("primary")}
                                        >
                                            + Add Primary Skill
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Secondary Skills */}
                            <div className="full-width">
                                <h3 className="form-section-title form-section-gap">Secondary Skills (Max 3)</h3>
                                {isNonTechnical
                                    ? !formData.role && (
                                        <p className="info-hint">
                                            Select a role above to enable skill selection
                                        </p>
                                    )
                                    : !formData.domain && skills.length === 0 && (
                                        /* SM-SKILL-MATCH: hidden when matched skills are shown
                                           (the Primary hint explains them).
                                           REVERT: remove  && skills.length === 0  */
                                        <p className="info-hint">
                                            Select a domain above to enable skill selection
                                        </p>
                                    )}
                                <div className="skill-block">
                                    {secondarySkills.map((item, index) =>
                                        renderSkillRow(item, index, "secondary")
                                    )}
                                    {secondarySkills.length < 3 && !isSubmitted && (
                                        <button
                                            type="button"
                                            className="add-skill-btn"
                                            onClick={() => addSkill("secondary")}
                                        >
                                            + Add Secondary Skill
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="form-group full-width">
                                <label htmlFor="additionalSkill">
                                    Additional Skill
                                </label>

                                <input
                                    id="additionalSkill"
                                    type="text"
                                    name="additionalSkill"
                                    placeholder="Enter additional skill"
                                    value={formData.additionalSkill || ""}
                                    onChange={handleChange}
                                    maxLength={255}
                                    disabled={isSubmitted}
                                />
                            </div>

                            {/* Soft Skill — required for Non-Technical employees,
                                who have no Domain / Service Line / Role above. */}
                            <div className="form-group full-width">
                                <label htmlFor="softSkill">
                                    Soft Skill
                                    {isNonTechnical && <span className="required-star">*</span>}
                                </label>

                                <select
                                    id="softSkill"
                                    name="softSkill"
                                    value={formData.softSkill || ""}
                                    onChange={handleChange}
                                    disabled={isSubmitted}
                                >
                                    <option value="">Select Soft Skill</option>

                                    {softSkills.map((skill) => (
                                        <option
                                            key={skill.softSkillId}
                                            value={skill.softSkillName}
                                        >
                                            {skill.softSkillName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Certification & Training */}
                            <h3 className="full-width form-section-title form-section-gap">
                                Certification & Training
                            </h3>

                            <div className="form-group">
                                <label htmlFor="certification">
                                    Last Completed Certification<span className="required-star">*</span>
                                </label>

                                <select
                                    id="certification"
                                    name="certification"
                                    value={formData.certification || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, certification: e.target.value })
                                    }
                                    disabled={isSubmitted}
                                >
                                    <option value="">Select</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>

                                {formData.certification === "Yes" && (
                                    <div className="nested-input">
                                        <label htmlFor="certificationName">
                                            Certificate Name
                                        </label>

                                        <input
                                            id="certificationName"
                                            name="certificationName"
                                            placeholder="Enter certificate name"
                                            value={formData.certificationName || ""}
                                            onChange={handleChange}
                                            maxLength={255}
                                            disabled={isSubmitted}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="form-group domain-field">
                                <label htmlFor="trainingReceived">
                                    Most Recent Training<span className="required-star">*</span>
                                </label>

                                <select
                                    id="trainingReceived"
                                    name="trainingReceived"
                                    value={formData.trainingReceived || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, trainingReceived: e.target.value })
                                    }
                                    disabled={isSubmitted}
                                >
                                    <option value="">Select</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>

                                {formData.trainingReceived === "Yes" && (
                                    <div className="nested-input">
                                        <label htmlFor="trainingName">
                                            Training Name
                                        </label>

                                        <input
                                            id="trainingName"
                                            name="trainingName"
                                            placeholder="Enter training name"
                                            value={formData.trainingName || ""}
                                            onChange={handleChange}
                                            maxLength={255}
                                            disabled={isSubmitted}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="form-group full-width button-row">
                                <button
                                    type="button"
                                    className="submit-btn small-btn back-btn"
                                    onClick={() => goToStep(1)}
                                >
                                    ← Back
                                </button>

                                {!isSubmitted && (
                                    <button
                                        type="submit"
                                        className="submit-btn big-btn save-btn"
                                        disabled={loading}
                                    >
                                        {loading ? "Submitting..." : "Submit Details"}
                                    </button>
                                )}
                            </div>
                        </>


                    )}
                </form>
            </div>
        </div>
    );
}